import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Form, 
  Input, 
  Select, 
  Button, 
  Table, 
  Space, 
  message, 
  Typography, 
  Divider,
  Checkbox,
  Row,
  Col,
  Tag,
  Tooltip
} from 'antd';
import { 
  SearchOutlined, 
  ReloadOutlined, 
  CheckSquareOutlined,
  BorderOutlined
} from '@ant-design/icons';
import { SAPB1Service, SapField, SapConfig } from '../services/SAPB1Service';
import { authService } from '../services/authService';
import { serviceService } from '../services/serviceService';
// import { useAuth } from '../hooks/useAuth';
import { useI18n } from '../hooks/useI18n';

const { Title, Text } = Typography;
const { Option } = Select;

interface SapFieldSelectorProps {
  onFieldsSelected?: (fields: SapField[]) => void;
  onFieldSelect?: (field: SapField) => void;
  selectedFields?: SapField[];
  multiple?: boolean;
  showServerSelector?: boolean;
  showAdvancedConfig?: boolean; // 新增：是否显示高级配置
}

const SapFieldSelector: React.FC<SapFieldSelectorProps> = ({
  onFieldsSelected,
  onFieldSelect,
  selectedFields = [],
  multiple = true,
  showServerSelector = true,
  showAdvancedConfig = false
}) => {
  const { t } = useI18n();
  // const { currentTenant } = useAuth();
  const currentTenant = authService.getCurrentTenant() || '';
  // const [form] = Form.useForm();
  
  // 状态管理
  const [loading, setLoading] = useState(false);
  const [services, setServices] = useState<Array<{id: number; service_name: string; service_url: string}>>([]);
  const [sapFields, setSapFields] = useState<SapField[]>([]);
  const [filteredFields, setFilteredFields] = useState<SapField[]>([]);
  const [selectedServiceId, setSelectedServiceId] = useState<number | undefined>();
  const [searchText, setSearchText] = useState('');
  const [fieldTypeFilter, setFieldTypeFilter] = useState<string>('');
  const [requiredOnly, setRequiredOnly] = useState(false);
  const [availableCompanies, setAvailableCompanies] = useState<string[]>([]);
  // 使用固定的配置值
  const [sapConfig, setSapConfig] = useState<SapConfig>({
    company: '',
    tableId: 'ODRF', // 固定值
    lang: 'en_US',
    fieldType: 'document' // 固定值：文档字段
  });

  // 加载服务列表和公司代码列表
  useEffect(() => {
    loadServices();
    loadAvailableCompanies();
  }, [currentTenant]);

  // 过滤字段
  useEffect(() => {
    const filtered = SAPB1Service.filterSapFields(sapFields, {
      searchText,
      fieldType: fieldTypeFilter || undefined,
      requiredOnly
    });
    setFilteredFields(filtered);
  }, [sapFields, searchText, fieldTypeFilter, requiredOnly]);

  const loadServices = async () => {
    try {
      const response = await serviceService.getServices();
      if (response.services) {
        setServices(response.services);
        // 自动选择第一个服务
        if (response.services.length > 0) {
          setSelectedServiceId(response.services[0].id);
        }
      }
    } catch (error) {
      console.error('加载服务列表失败:', error);
      message.error(t('sap.selector.loadServicesFailed'));
    }
  };

  const loadAvailableCompanies = async () => {
    try {
      if (!currentTenant) {
        message.error(t('tenantSelector.noTenants'));
        return;
      }
      const response = await SAPB1Service.getAvailableCompanies(currentTenant);
      if (response.success && response.data.companies) {
        setAvailableCompanies(response.data.companies);
        // 自动选择第一个公司代码
        if (response.data.companies.length > 0) {
          setSapConfig(prev => ({ ...prev, company: response.data.companies[0] }));
        }
      }
    } catch (error) {
      console.error('加载公司代码列表失败:', error);
      message.error(t('sap.selector.loadCompaniesFailed'));
    }
  };

  const fetchSapFields = async () => {
    if (!currentTenant) {
      message.error(t('tenantSelector.noTenants'));
      return;
    }
    // 验证配置
    const validation = SAPB1Service.validateSapConfigLocal(sapConfig);
    if (!validation.isValid) {
      message.error(t('sap.selector.configInvalid', { errors: validation.errors.join(', ') }));
      return;
    }

    setLoading(true);
    try {
      // 不再需要serverId，后台会自动获取服务配置
      const response = await SAPB1Service.getSapFields(currentTenant, {
        ...sapConfig
      });

      if (response.success) {
        setSapFields(response.data.fields);
        message.success(t('sap.selector.fetchFieldsSuccess', { count: response.data.fields.length }));
      } else {
        // 获取失败时清除之前的字段数据
        setSapFields([]);
        setFilteredFields([]);
        message.error(t('sap.selector.fetchFieldsFailed'));
      }
    } catch (error: unknown) {
      console.error('获取SAP字段失败:', error);
      // 发生异常时也清除之前的字段数据
      setSapFields([]);
      setFilteredFields([]);
      message.error((error as Error).message || t('sap.selector.fetchFieldsFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleFieldSelect = (field: SapField) => {
    if (multiple) {
      const isSelected = selectedFields.some(f => f.FieldID === field.FieldID);
      let newSelectedFields: SapField[];
      
      if (isSelected) {
        newSelectedFields = selectedFields.filter(f => f.FieldID !== field.FieldID);
      } else {
        newSelectedFields = [...selectedFields, field];
      }
      
      onFieldsSelected?.(newSelectedFields);
    } else {
      onFieldSelect?.(field);
    }
  };

  const handleSelectAll = () => {
    if (multiple) {
      const allSelected = filteredFields.every(field => 
        selectedFields.some(f => f.FieldID === field.FieldID)
      );
      
      if (allSelected) {
        // 取消选择所有过滤后的字段
        const newSelectedFields = selectedFields.filter(field => 
          !filteredFields.some(f => f.FieldID === field.FieldID)
        );
        onFieldsSelected?.(newSelectedFields);
      } else {
        // 选择所有过滤后的字段
        const newSelectedFields = [...selectedFields];
        filteredFields.forEach(field => {
          if (!newSelectedFields.some(f => f.FieldID === field.FieldID)) {
            newSelectedFields.push(field);
          }
        });
        onFieldsSelected?.(newSelectedFields);
      }
    }
  };

  const columns = [
    {
      title: multiple ? (
        <Checkbox
          checked={filteredFields.length > 0 && filteredFields.every(field => 
            selectedFields.some(f => f.FieldID === field.FieldID)
          )}
          indeterminate={
            filteredFields.some(field => 
              selectedFields.some(f => f.FieldID === field.FieldID)
            ) && !filteredFields.every(field => 
              selectedFields.some(f => f.FieldID === field.FieldID)
            )
          }
          onChange={handleSelectAll}
        />
      ) : t('sap.selector.colSelect'),
      key: 'select',
      width: 60,
      render: (record: SapField) => {
        const isSelected = selectedFields.some(f => f.FieldID === record.FieldID);
        return (
          <Checkbox
            checked={isSelected}
            onChange={() => handleFieldSelect(record)}
          />
        );
      }
    },
    {
      title: t('sap.selector.colFieldId'),
      dataIndex: 'FieldID',
      key: 'FieldID',
      width: 80,
      sorter: (a: SapField, b: SapField) => a.FieldID - b.FieldID
    },
    {
      title: t('sap.selector.colAliasId'),
      dataIndex: 'AliasID',
      key: 'AliasID',
      width: 150,
      sorter: (a: SapField, b: SapField) => a.AliasID.localeCompare(b.AliasID)
    },
    {
      title: t('sap.selector.colDescr'),
      dataIndex: 'Descr',
      key: 'Descr',
      ellipsis: true,
      render: (text: string) => (
        <Tooltip title={text}>
          <Text>{text}</Text>
        </Tooltip>
      )
    },
    {
      title: t('sap.selector.colType'),
      dataIndex: 'FieldType',
      key: 'FieldType',
      width: 100,
      render: (type: string) => (
        <Tag color="blue">{type}</Tag>
      )
    },
    {
      title: t('sap.selector.colSchemaType'),
      key: 'schemaType',
      width: 100,
      render: (record: SapField) => (
        <Tag color="green">{SAPB1Service.mapSapTypeToSchemaType(record.FieldType)}</Tag>
      )
    },
    {
      title: t('sap.selector.colSize'),
      dataIndex: 'SizeID',
      key: 'SizeID',
      width: 80,
      sorter: (a: SapField, b: SapField) => a.SizeID - b.SizeID
    },
    {
      title: t('sap.selector.colRequired'),
      dataIndex: 'NotNull',
      key: 'NotNull',
      width: 80,
      render: (value: string) => (
        <Tag color={value === 'Y' ? 'red' : 'default'}>
          {value === 'Y' ? t('common.yes') : t('common.no')}
        </Tag>
      )
    }
  ];

  const getUniqueFieldTypes = () => {
    const types = [...new Set(sapFields.map(field => field.FieldType))];
    return types.sort();
  };

  return (
    <Card title={t('sap.selector.title')} size="small">
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        {/* 服务选择 - 仅在高级模式下显示 */}
        {showServerSelector && showAdvancedConfig && (
          <Form.Item label={t('sap.selector.service')}>
            <Select
              placeholder={t('sap.selector.chooseService')}
              value={selectedServiceId}
              onChange={setSelectedServiceId}
              style={{ width: 300 }}
            >
              {services.map(service => (
                <Option key={service.id} value={service.id}>
                  {service.service_name} ({service.service_url})
                </Option>
              ))}
            </Select>
          </Form.Item>
        )}

        {/* SAP配置 - 简化版本 */}
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item label={t('sap.selector.company')}>
              <Select
                value={sapConfig.company}
                onChange={(value) => setSapConfig({ ...sapConfig, company: value })}
                placeholder={t('sap.selector.chooseCompany')}
                style={{ width: '100%' }}
                loading={availableCompanies.length === 0}
              >
                {availableCompanies.map(company => (
                  <Option key={company} value={company}>
                    {company}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label={t('sap.selector.language')}>
              <Select
                value={sapConfig.lang}
                onChange={(value) => setSapConfig({ ...sapConfig, lang: value })}
                style={{ width: '100%' }}
              >
                <Option value="en_US">English</Option>
                <Option value="zh_CN">中文</Option>
                <Option value="zh_TW">繁體中文</Option>
                <Option value="ja_JP">日本語</Option>
                <Option value="ko_KR">한국어</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label={t('sap.selector.actions')}>
              <Space>
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  onClick={fetchSapFields}
                  loading={loading}
                >
                  {t('sap.selector.fetchFields')}
                </Button>
                {showAdvancedConfig && (
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={loadServices}
                  >
                    {t('sap.selector.refreshServices')}
                  </Button>
                )}
              </Space>
            </Form.Item>
          </Col>
        </Row>

        {/* 高级配置 - 仅在高级模式下显示 */}
        {showAdvancedConfig && (
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label={t('sap.selector.tableId')}>
                <Input
                  value={sapConfig.tableId}
                  onChange={(e) => setSapConfig({ ...sapConfig, tableId: e.target.value })}
                  placeholder="ODRF"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label={t('sap.selector.fieldType')}>
                <Select
                  value={sapConfig.fieldType}
                  onChange={(value) => setSapConfig({ ...sapConfig, fieldType: value })}
                  style={{ width: '100%' }}
                >
                  <Option value="document">{t('sap.selector.fieldTypeDocument')}</Option>
                  <Option value="table">{t('sap.selector.fieldTypeTable')}</Option>
                  <Option value="business_object">{t('sap.selector.fieldTypeBusinessObject')}</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
        )}

        {/* 字段列表 */}
        {sapFields.length > 0 && (
          <>
            <Divider />
            <Row gutter={16} align="middle">
              <Col span={12}>
                <Input
                  placeholder={t('sap.selector.searchPlaceholder')}
                  prefix={<SearchOutlined />}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  style={{ width: 300 }}
                />
              </Col>
              <Col span={6}>
                <Select
                  placeholder={t('sap.selector.filterByType')}
                  value={fieldTypeFilter}
                  onChange={setFieldTypeFilter}
                  allowClear
                  style={{ width: 150 }}
                >
                  {getUniqueFieldTypes().map(type => (
                    <Option key={type} value={type}>{type}</Option>
                  ))}
                </Select>
              </Col>
              <Col span={6}>
                <Checkbox
                  checked={requiredOnly}
                  onChange={(e) => setRequiredOnly(e.target.checked)}
                >
                  {t('sap.selector.requiredOnly')}
                </Checkbox>
              </Col>
            </Row>

            <div style={{ marginTop: 16 }}>
              <Space>
                <Text strong>{t('sap.selector.selectedCount', { count: selectedFields.length })}</Text>
                <Text type="secondary">{t('sap.selector.displayCount', { count: filteredFields.length })}</Text>
                {multiple && (
                  <Button
                    size="small"
                    onClick={handleSelectAll}
                    icon={
                      filteredFields.length > 0 && filteredFields.every(field => 
                        selectedFields.some(f => f.FieldID === field.FieldID)
                      ) ? <CheckSquareOutlined /> : <BorderOutlined />
                    }
                  >
                    {filteredFields.length > 0 && filteredFields.every(field => 
                      selectedFields.some(f => f.FieldID === field.FieldID)
                    ) ? t('sap.selector.unselectAll') : t('sap.selector.selectAllPage')}
                  </Button>
                )}
              </Space>
            </div>

            <Table
              columns={columns}
              dataSource={filteredFields}
              rowKey="FieldID"
              size="small"
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => 
                  t('sap.selector.paginationTotal', { from: range[0], to: range[1], total })
              }}
              scroll={{ y: 400 }}
            />
          </>
        )}

        {/* 选中字段摘要 */}
        {selectedFields.length > 0 && (
          <>
            <Divider />
            <div>
              <Title level={5}>{t('sap.selector.selectedFieldsTitle', { count: selectedFields.length })}</Title>
              <Space wrap>
                {selectedFields.map(field => (
                  <Tag
                    key={field.FieldID}
                    closable
                    onClose={() => handleFieldSelect(field)}
                    color="blue"
                  >
                    {field.AliasID} ({field.FieldType})
                  </Tag>
                ))}
              </Space>
            </div>
          </>
        )}
      </Space>
    </Card>
  );
};

export default SapFieldSelector;
